import { useState, useCallback, useMemo } from "react";
import {
  ECHO_CARD_MAP, ECHO_CARDS, LEVEL_COSTS, MAX_CARD_LEVEL,
  RARITY, BOOST_COST, COLLECTION_MILESTONES,
  basePerLevel,
  CARDS_BY_RARITY, CARD_DROP_WEIGHTS, CARDS_BY_SOURCE,
} from "../data/echoCards";

const TOTAL_CARDS = ECHO_CARDS.length;

// ── Cost to level a card from its current level to next ───────────────────────
export function costToLevelUp(currentLevel) {
  if (currentLevel >= MAX_CARD_LEVEL) return null;
  return LEVEL_COSTS[currentLevel - 1]; // index 0 = cost for Lv1→Lv2
}

// ── Roll a random card from weighted rarity pool ──────────────────────────────
// eligibleIds: optional Set/array restricting which card IDs can be picked.
// Cards with sources:["any"] are always included when an eligibleIds filter is active.
function rollRandomCard(ownedCardIds, eligibleIds) {
  const anyIds = new Set(CARDS_BY_SOURCE.any ?? []);
  const filter = eligibleIds
    ? id => !ownedCardIds.has(id) && (eligibleIds.has(id) || anyIds.has(id))
    : id => !ownedCardIds.has(id);

  const totalWeight = Object.values(CARD_DROP_WEIGHTS).reduce((a, b) => a + b, 0);
  let r = Math.random() * totalWeight;
  let rarity = "common";
  for (const [rar, w] of Object.entries(CARD_DROP_WEIGHTS)) {
    r -= w;
    if (r <= 0) { rarity = rar; break; }
  }
  const pool = (CARDS_BY_RARITY[rarity] ?? []).filter(filter);
  if (pool.length === 0) {
    // Rarity pool empty — fall back to common within the same eligible set
    const fallback = (CARDS_BY_RARITY.common ?? []).filter(filter);
    if (fallback.length === 0) return null;
    return fallback[Math.floor(Math.random() * fallback.length)];
  }
  return pool[Math.floor(Math.random() * pool.length)];
}

// ── Per-stat bonus contributed by a single card at a given level ──────────────
// Returns { [statKey]: bonusValue } (percent for % stats, flat for flat stats)
export function cardStatContribution(card, level, boosted = false) {
  if (level < 1) return {};
  const rarityCfg = RARITY[card.rarity];
  const mult = rarityCfg?.statMult ?? 1;
  const boostMult = boosted ? 1.5 : 1.0;
  const stats = Array.isArray(card.stat) ? card.stat : [card.stat];

  let total = 0;
  for (let lv = 1; lv <= level; lv++) {
    total += basePerLevel(card.statType, lv) * mult * boostMult;
  }
  total = Math.round(total * 10) / 10;

  const result = {};
  for (const s of stats) result[s] = total;
  return result;
}

// ── Ability unlock check ──────────────────────────────────────────────────────
export function isAbilityUnlocked(card, abilityIndex, level) {
  const unlockLv = RARITY[card.rarity]?.abilityLv[abilityIndex] ?? 999;
  return level >= unlockLv;
}

// ── Active card slot limit (from researchUnlocks or fallback) ─────────────────
export function maxEquippedSlots(researchUnlocks) {
  return researchUnlocks?.activeCardSlots ?? 1;
}

// ── Collection milestone multipliers ─────────────────────────────────────────
// Returns { all, resource, combat, echo } compound multipliers for N unique cards owned
export function getMilestoneMultipliers(uniqueCardsOwned) {
  const mults = { all: 1, resource: 1, combat: 1, echo: 1 };
  for (const m of COLLECTION_MILESTONES) {
    if (uniqueCardsOwned >= m.cards) {
      if (m.category === "all") {
        mults.all *= m.mult;
      } else {
        mults[m.category] *= m.mult;
      }
    }
  }
  return mults;
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useEchoCards({ savedEchoCurrency, savedVoidShards, savedOwnedCards, savedEquippedCards, savedBoostedCards, savedClaimedVoidRewards }) {
  // echoCurrency: spendable resource for leveling
  const [echoCurrency, setEchoCurrency]   = useState(savedEchoCurrency ?? 0);
  // voidShards: premium currency
  const [voidShards,   setVoidShards]     = useState(savedVoidShards   ?? 0);
  // ownedCards: { [cardId]: level (1–20) }
  const [ownedCards,   setOwnedCards]     = useState(savedOwnedCards   ?? {});
  // equippedCards: array of cardIds currently in active slots
  const [equippedCards, setEquippedCards] = useState(savedEquippedCards ?? []);
  // boostedCards: Set of cardIds that have been premium-boosted
  const [boostedCards, setBoostedCards]   = useState(() => new Set(savedBoostedCards ?? []));
  // claimedVoidRewards: Set of reward keys that have already been granted
  const [claimedVoidRewards, setClaimedVoidRewards] = useState(() => new Set(savedClaimedVoidRewards ?? []));

  // ── Award Echo Currency (from any gameplay source) ─────────────────────────
  const addEchoCurrency = useCallback((amount) => {
    if (amount <= 0) return;
    setEchoCurrency(prev => prev + amount);
  }, []);

  // ── Award Void Shards ─────────────────────────────────────────────────────
  const addVoidShards = useCallback((amount) => {
    if (amount <= 0) return;
    setVoidShards(prev => prev + amount);
  }, []);

  // ── Claim a one-time Void Shard reward by key ─────────────────────────────
  // Returns true if newly claimed (shards awarded), false if already claimed.
  const claimVoidReward = useCallback((key, amount) => {
    let claimed = false;
    setClaimedVoidRewards(prev => {
      if (prev.has(key)) return prev;
      claimed = true;
      return new Set([...prev, key]);
    });
    if (claimed) setVoidShards(prev => prev + amount);
    return claimed;
  }, []);

  // ── Grant a card (from drop or milestone) ────────────────────────────────
  // Returns the card object if it was newly added, null if already owned.
  const grantCard = useCallback((cardId) => {
    const card = ECHO_CARD_MAP[cardId];
    if (!card) return null;
    let newCount = null;
    setOwnedCards(prev => {
      if (prev[cardId] != null) return prev; // already owned
      const next = { ...prev, [cardId]: 1 };
      newCount = Object.keys(next).length;
      return next;
    });
    if (newCount != null) {
      // ◈3 per collection milestone reached for the first time
      for (const m of COLLECTION_MILESTONES) {
        if (newCount === m.cards) {
          claimVoidReward(`milestone_${m.cards}`, 3);
        }
      }
      // ◈25 for owning all cards
      if (newCount === TOTAL_CARDS) {
        claimVoidReward("full_collection", 25);
      }
      return card;
    }
    return null;
  }, [claimVoidReward]);

  // ── Roll a random card drop ───────────────────────────────────────────────
  // source: optional string (material ID, "combat", "smelt") to restrict pool.
  // Returns the newly granted card object, or null.
  const rollCardDrop = useCallback((source) => {
    const owned = new Set(Object.keys(ownedCards));
    let eligibleIds = null;
    if (source) {
      const specific = CARDS_BY_SOURCE[source] ?? [];
      eligibleIds = new Set(specific);
    }
    const cardId = rollRandomCard(owned, eligibleIds);
    if (!cardId) return null;
    return grantCard(cardId);
  }, [ownedCards, grantCard]);

  // ── Level up a card (costs echoCurrency) ─────────────────────────────────
  // Returns { success, cost, newLevel } or { success: false }
  const levelUpCard = useCallback((cardId) => {
    const currentLv = ownedCards[cardId];
    if (currentLv == null || currentLv >= MAX_CARD_LEVEL) return { success: false };
    const cost = costToLevelUp(currentLv);
    if (cost == null || echoCurrency < cost) return { success: false, reason: "insufficient" };
    setEchoCurrency(prev => prev - cost);
    const newLevel = currentLv + 1;
    setOwnedCards(prev => {
      const next = { ...prev, [cardId]: newLevel };
      // ◈50 if all owned cards are now at max level
      if (newLevel === MAX_CARD_LEVEL) {
        const allMaxed = Object.values(next).every(lv => lv >= MAX_CARD_LEVEL);
        if (allMaxed && Object.keys(next).length === TOTAL_CARDS) {
          claimVoidReward("all_cards_max", 50);
        }
      }
      return next;
    });

    // ◈2 at first Lv10, ◈5 at first Lv20 (per card)
    if (newLevel === 10) claimVoidReward(`lv10_${cardId}`, 2);
    if (newLevel === MAX_CARD_LEVEL) claimVoidReward(`lv20_${cardId}`, 5);

    return { success: true, cost, newLevel };
  }, [ownedCards, echoCurrency, claimVoidReward]);

  // ── Boost a card with Void Shards ─────────────────────────────────────────
  const boostCard = useCallback((cardId) => {
    const card = ECHO_CARD_MAP[cardId];
    if (!card || boostedCards.has(cardId)) return { success: false };
    const cost = BOOST_COST[card.rarity] ?? 5;
    if (voidShards < cost) return { success: false, reason: "insufficient" };
    setVoidShards(prev => prev - cost);
    setBoostedCards(prev => new Set([...prev, cardId]));
    return { success: true };
  }, [boostedCards, voidShards]);

  // ── Equip / unequip a card ────────────────────────────────────────────────
  const toggleEquipped = useCallback((cardId, maxSlots) => {
    setEquippedCards(prev => {
      if (prev.includes(cardId)) return prev.filter(id => id !== cardId);
      if (prev.length >= maxSlots) return prev;
      return [...prev, cardId];
    });
  }, []);

  // ── Derived: all owned cards with metadata ────────────────────────────────
  const ownedCardList = useMemo(() => {
    return Object.entries(ownedCards).map(([id, level]) => {
      const card = ECHO_CARD_MAP[id];
      return { card, level, boosted: boostedCards.has(id) };
    }).filter(e => e.card != null);
  }, [ownedCards, boostedCards]);

  // ── Derived: collection milestone multipliers ─────────────────────────────
  const milestoneMultipliers = useMemo(() => {
    return getMilestoneMultipliers(ownedCardList.length);
  }, [ownedCardList.length]);

  // ── Derived: stat bonuses from all owned (passive) + equipped (active) ────
  // equippedBonuses: contribution from equipped cards only
  // collectionBonuses: flat % bonuses from collection milestones
  const cardStatBonuses = useMemo(() => {
    const bonuses = {}; // { [statKey]: totalBonus% or flatBonus }
    for (const { card, level, boosted } of ownedCardList) {
      const contrib = cardStatContribution(card, level, boosted);
      for (const [stat, val] of Object.entries(contrib)) {
        bonuses[stat] = (bonuses[stat] ?? 0) + val;
      }
    }
    return bonuses;
  }, [ownedCardList]);

  return {
    echoCurrency,
    voidShards,
    ownedCards,
    equippedCards,
    boostedCards,
    claimedVoidRewards,
    ownedCardList,
    milestoneMultipliers,
    cardStatBonuses,
    addEchoCurrency,
    addVoidShards,
    claimVoidReward,
    grantCard,
    rollCardDrop,
    levelUpCard,
    boostCard,
    toggleEquipped,
    setEchoCurrency,
    setVoidShards,
    setOwnedCards,
    setEquippedCards,
    setBoostedCards,
    setClaimedVoidRewards,
  };
}
